import { Track } from "@/src/lib/tracking";
import { ROUTER_PATH } from "@config/router";
import { ShellLink } from "../../ShellNavigation/ShellAwareLink";
import { getDisplayPlanName } from "../shared/planDisplayUtils";
import { cn, pubsub } from "utils";
import { useCallback, useEffect } from "react";
import { MemberPlanInfo } from "@hooks/useMemberUser";
import { useIsPc } from "@hooks/useIsPc";
import { openManageBillingModal } from "./ManageBillingModal";
import { useParams } from "next/navigation";
import { OspToast } from "@ospweb/sdk-ui";
import { EquityGrantCodeEnum } from "@features/api/memberApi";
import { useMemberGrantEquity } from "@hooks/useMemberGrantEquity";
import { useGetListPayment } from "@hooks/useGetListPayment";
import { useUpdateAutoRenew } from "@hooks/useSubscriptionManage";
import { openCancelFeedbackSurveyModal } from "./subscriptionRetention/CancelFeedbackSurveyModalCreateAble";
import { openRetentionCreditsOfferModal } from "./subscriptionRetention/RetentionCreditsOfferModalCreateAble";
import { runCancelSubscriptionFlow } from "./subscriptionRetention/runCancelSubscriptionFlow";
import { pubSubKeyMap } from "@utils/pubSubKeyMap";

export const ManageBillingUpgrade = ({
  isFree,
  toMemberPrice,
  data,
}: {
  isFree: boolean;
  toMemberPrice: () => void;
  data?: MemberPlanInfo;
}) => {
  const [isPc] = useIsPc();
  const { locale } = useParams<{ locale: string }>();
  const { queryResult: retentionCredits } = useMemberGrantEquity({
    code: EquityGrantCodeEnum.MEMBER_CANCEL_SUBSCRIPTION_RETENTION_CREDIT,
    enabled: isPc && !isFree,
  });
  const { cancelSubscription } = useGetListPayment();
  const { updateAutoRenew } = useUpdateAutoRenew();

  // 处理"管理支付方式"点击：使用 EdgenModalCreateAble 打开账单管理弹窗。
  // 弹窗内「Turn off auto-renew」会关闭弹窗并带出条目，这里接挽留 → 问卷 → PATCH（EDG-552：PC 首次接入挽留）
  const handleManageBilling = useCallback(async () => {
    Track.log("info", "Member", "[trace] handle Manage Billing open");
    if (!isPc) {
      return;
    }
    const result = await openManageBillingModal({ position: "pc_modal" });
    if (result.action !== "CancelSubscription" || !result.payload) {
      return;
    }
    await runCancelSubscriptionFlow(result.payload, {
      retentionCredits:
        typeof retentionCredits.data === "number"
          ? retentionCredits.data
          : undefined,
      openRetentionCreditsOfferModal,
      openCancelFeedbackSurveyModal,
      turnOffAutoRenew: (item) => updateAutoRenew(item, false),
      legacyCancel: (item) =>
        cancelSubscription({
          pay_channel: item.pay_channel,
          links: item.links,
        }),
      onTurnedOff: (message) => OspToast.success({ content: message }),
    });
  }, [isPc, retentionCredits.data, updateAutoRenew, cancelSubscription]);

  // 跨渠道下单引导里的「Manage subscription」（PC）：与点「Manage Billing」同一入口
  useEffect(() => {
    if (!isPc || isFree) {
      return;
    }
    return pubsub.on(pubSubKeyMap.MEMBER_OPEN_MANAGE_BILLING, () => {
      void handleManageBilling();
    });
  }, [isPc, isFree, handleManageBilling]);

  if (isFree) {
    return (
      <button
        type="button"
        className={cn(
          "button primary_button button_m shrink-0",
          isPc ? "xs_button" : "md_button",
        )}
        onClick={toMemberPrice}
      >
        {"Upgrade"}
      </button>
    );
  }
  return (
    <>
      {isPc ? (
        <button
          type="button"
          className={"button xs_button button_m shrink-0 secondary_button"}
          onClick={handleManageBilling}
        >
          {"Manage Billing"}
        </button>
      ) : (
        <ShellLink
          prefetch
          href={`/${locale}${ROUTER_PATH.MEMBER_SUBSCRIPTION}?productName=${getDisplayPlanName(data?.level)}`}
          data-openreplay-label="member_currentplan_managebilling"
          className="button xs_button button_m shrink-0 border-none"
        >
          {"Manage Billing"}
        </ShellLink>
      )}
    </>
  );
};
